import { Box, Grid, Popper, Stack, SxProps, TablePaginationProps, Theme } from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridColumnVisibilityModel,
  GridPagination,
  GridPaginationModel,
  GridRow,
  GridRowClassNameParams,
  GridRowParams,
  GridRowProps,
  GridRowsProp,
  GridSortModel,
  GridValidRowModel,
  PaginationPropsOverrides,
  useGridApiRef,
} from '@mui/x-data-grid';
import React, {
  ReactElement,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useMeasure } from 'react-use';

interface GridDataProps<T extends GridValidRowModel> {
  header?: ReactNode;
  isLoading?: boolean;
  rows?: GridRowsProp<T>;
  rowHeight?: number;
  columnHeaderHeight?: number;
  autoHeight?: boolean;
  minHeight?: number;
  onRowClick?: (r: GridRowParams<T>) => void;
  getRowId?: (row: T) => string;
  getRowClassName?: (params: GridRowClassNameParams<T>) => string;
  columns: (GridColDef<T> & { initVisibility?: boolean })[];
  renderAction?: (r: T) => React.ReactNode;
  onColumnVisibilityModelChange?: (model: GridColumnVisibilityModel) => void;
  sorting?: {
    value: GridSortModel | string | undefined;
    onChange: (s: GridSortModel) => void;
  };
  pagination:
    | ({
        totalCount?: number;
        onChange: (p: GridPaginationModel) => void;
      } & GridPaginationModel)
    | null;
  paginationLeftPart?: ReactNode;
  checkboxSelection?: {
    rowSelectionModel: string[];
    hideSelectAllCheckbox?: boolean;
    hideFooterSelectedRowCount?: boolean;
    isRowSelectable?: (params: GridRowParams<T>) => boolean;
    onRowSelectionModelChange: (value: string[]) => void;
  };
  enableColumnFiltering?: boolean;
  noRowsOverlay?: React.ComponentType;
  sx?: SxProps<Theme>;
}

export const GridData = <T extends GridValidRowModel>(
  props: GridDataProps<T>,
): ReactElement | null => {
  const {
    header,
    isLoading,
    rows = [],
    rowHeight,
    columnHeaderHeight,
    autoHeight = false,
    minHeight = 400,
    onRowClick,
    getRowId,
    getRowClassName,
    columns,
    renderAction,
    onColumnVisibilityModelChange,
    sorting,
    pagination,
    paginationLeftPart,
    checkboxSelection,
    enableColumnFiltering = false,
    noRowsOverlay,
    sx,
  } = props;
  const [containerRef, { height: containerHeight }] = useMeasure();
  const [headerRef, { height: headerHeight }] = useMeasure();
  const apiRef = useGridApiRef();

  const initialColumnVisibilityModel = useMemo(() => {
    return columns.reduce<GridColumnVisibilityModel>((acc, c) => {
      if (typeof c.initVisibility === 'boolean') {
        acc[c.field] = c.initVisibility;
      }
      return acc;
    }, {});
  }, [columns]);

  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>(
    initialColumnVisibilityModel,
  );

  const [hasUserChanges, setHasUserChanges] = useState(false);

  const columnFields = useMemo(() => columns.map(c => c.field), [columns]);
  const [previousColumnFields, setPreviousColumnFields] = useState(columnFields);

  useEffect(() => {
    const fieldsChanged =
      columnFields.length !== previousColumnFields.length ||
      columnFields.some((field, index) => field !== previousColumnFields[index]);

    if (fieldsChanged) {
      setHasUserChanges(false);
      setPreviousColumnFields(columnFields);
    }
  }, [columnFields, previousColumnFields]);

  useEffect(() => {
    if (!hasUserChanges) {
      setColumnVisibilityModel(initialColumnVisibilityModel);
    }
  }, [initialColumnVisibilityModel, hasUserChanges]);

  const handleColumnVisibilityModelChange = (newModel: GridColumnVisibilityModel) => {
    setHasUserChanges(true);
    setColumnVisibilityModel(newModel);
    onColumnVisibilityModelChange?.(newModel);
  };

  const sortModel: GridSortModel | undefined = useMemo(() => {
    if (sorting) {
      if (typeof sorting.value === 'string') {
        // Split on the LAST underscore so multi-underscore field names
        // (e.g. "application_name_asc") parse to field="application_name",
        // sort="asc" rather than field="application", sort="name".
       const separatorIndex = sorting.value.lastIndexOf('_');
       const field = sorting.value.slice(0, separatorIndex);
       const sort = sorting.value.slice(separatorIndex + 1) as 'asc' | 'desc';


        return [
          {
            field,
            sort,
          },
        ];
      }

      return sorting.value;
    }

    return undefined;
  }, [sorting?.value]);

  // The DataGrid prunes a controlled sortModel that references a column it has
  // not registered yet and emits a spurious onSortModelChange([]) — both when
  // the column arrives in the same commit (it ingests columns after applying
  // the model) and when the sort model lands a commit earlier. Holding back
  // unknown fields and deferring the model by one effect tick covers both.
  const safeSortModel = useMemo(
    () => sortModel?.filter(item => columns.some(c => c.field === item.field)),
    [sortModel, columns],
  );

  const [appliedSortModel, setAppliedSortModel] = useState<GridSortModel | undefined>(
    () => safeSortModel,
  );

  useEffect(() => {
    setAppliedSortModel(safeSortModel);
  }, [safeSortModel]);

  return (
    <Box
      ref={containerRef}
      sx={{
        height: autoHeight ? 'auto' : '100%',
        minHeight: autoHeight ? undefined : `${minHeight}px`,
        borderRadius: 1.5,
        border: t => `1px solid ${t.palette.grey[200]}`,
        overflow: 'hidden',
      }}
    >
      {!!header && (
        <Box ref={headerRef}>
          <Box sx={{ padding: '15px' }}>{header}</Box>
        </Box>
      )}

      <Box sx={{ position: 'relative' }}>
        <DataGrid
          apiRef={apiRef}
          loading={isLoading}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
          rows={rows}
          columns={columns}
          getRowId={getRowId}
          sortingMode="server"
          sortModel={appliedSortModel}
          sortingOrder={['asc', 'desc']}
          onSortModelChange={sorting?.onChange}
          onRowClick={onRowClick}
          hideFooter={pagination === null}
          getRowClassName={params => {
            const stripeClass =
              params.indexRelativeToCurrentPage % 2 === 1 ? 'odd-row' : 'even-row';
            const customClass = getRowClassName?.(params);

            return [stripeClass, customClass].filter(Boolean).join(' ');
          }}
          {...(pagination !== null && {
            pagination: true,
            paginationMode: 'server',
            paginationModel: {
              page: pagination?.page || 0,
              pageSize: pagination?.pageSize || 25,
            },
            pageSizeOptions: [pagination?.pageSize || 25],
            rowCount: pagination?.totalCount || 0,
            onPaginationModelChange: pagination?.onChange,
          })}
          disableColumnFilter={!enableColumnFiltering}
          disableColumnMenu={false}
          disableRowSelectionOnClick
          checkboxSelection={!!checkboxSelection}
          isRowSelectable={checkboxSelection?.isRowSelectable}
          rowSelectionModel={checkboxSelection?.rowSelectionModel}
          onRowSelectionModelChange={checkboxSelection?.onRowSelectionModelChange}
          hideFooterSelectedRowCount={checkboxSelection?.hideFooterSelectedRowCount}
          keepNonExistentRowsSelected
          rowHeight={rowHeight}
          columnHeaderHeight={columnHeaderHeight}
          autoHeight={autoHeight}
          slots={{
            ...(pagination === null ? {} : { pagination: CustomPagination }),
            row: CustomRow,
            ...(noRowsOverlay ? { noRowsOverlay } : {}),
          }}
          slotProps={{
            ...(pagination === null
              ? {}
              : { pagination: { paginationLeftPart } as unknown as TablePaginationProps }),
            row: {
              apiRef,
              renderAction,
            } as unknown as GridRowProps,
          }}
          sx={[{
            // Fixed-height grids need a valid measurement; compact grids grow with their rows.
            height: autoHeight
              ? 'auto'
              : containerHeight > 0
                ? `${Math.max(containerHeight - headerHeight, 200)}px`
                : `${minHeight}px`,
            fontSize: '12px',
            borderRadius: 0,

            '& .MuiDataGrid-iconButtonContainer .MuiIconButton-root': {
              opacity: 0.8,
              '&:hover': {
                opacity: 1,
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            },
            '& .MuiDataGrid-menuIconButton': {
              opacity: 0.8,
              '&:hover': {
                opacity: 1,
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            },
            '& .MuiDataGrid-columnHeaderTitleContainer': {
              '&:hover .MuiDataGrid-menuIconButton': {
                opacity: 1,
              },
              '&:hover .MuiDataGrid-iconButtonContainer .MuiIconButton-root': {
                opacity: 1,
              },
            },
            '& .MuiDataGrid-row:hover': {
              cursor: onRowClick ? 'pointer' : 'default',
            },
            '& .MuiDataGrid-cell:focus': {
              outline: 'none',
            },
            '& .MuiDataGrid-columnHeader .MuiCheckbox-root': {
              display: checkboxSelection?.hideSelectAllCheckbox ? 'none' : undefined,
            },
            '& .MuiDataGrid-selectedRowCount': {
              whiteSpace: 'nowrap',
            },
            '& .MuiDataGrid-columnsManagement': {
              '& .MuiFormControlLabel-root': {
                marginLeft: 0,
                marginRight: 0,
              },
            },
            '& .MuiDataGrid-columnsPanelRow': {
              padding: '4px 8px',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            },
            '& .odd-row': {
              backgroundColor: 'rgba(0, 0, 0, 0.02)',
            },
            '& .odd-row:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.06)',
            },
          }, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}
        />
      </Box>
    </Box>
  );
};

export default GridData;

const CustomRow = <T extends GridValidRowModel>(
  props: GridRowProps & {
    apiRef: ReturnType<typeof useGridApiRef>;
    renderAction?: (r: T) => React.ReactNode;
  },
) => {
  const { apiRef, renderAction, ...rest } = props;
  const [open, setOpen] = useState(false);
  const rowWrapperRef = useRef<HTMLDivElement>(null);

  const action = renderAction?.(rest.row as unknown as T);

  const rowHeight = typeof props.rowHeight === 'number' ? props.rowHeight : 52;

  const dimensions = apiRef.current?.getRootDimensions();
  const scrollPosition = apiRef.current?.getScrollPosition();

  const value = dimensions && scrollPosition 
    ? 25 - dimensions.viewportOuterSize.width - scrollPosition.left 
    : 25;

  return (
    <Box
      ref={rowWrapperRef}
      component="div"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocusCapture={() => setOpen(true)}
      onBlurCapture={event => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          setOpen(false);
        }
      }}
      sx={{
        width: '100%',
        height: '100%',
        position: 'relative',
      }}
    >
      <GridRow {...rest} />

      {!!action && (
        <Popper
          anchorEl={rowWrapperRef.current}
          open={open}
          placement="left"
          // The offset below assumes the left placement. Scrolled far right, the row has no room on
          // its left, and a flip to the right would put the actions off screen.
          modifiers={[{ name: 'flip', enabled: false }]}
        >
          <Stack
            sx={{
              position: 'fixed',
              height: `${rowHeight}px`,
              top: `-${rowHeight / 2}px`,
              right: `${value}px`,
            }}
          >
            <Box
              sx={{
                margin: 'auto',
                display: 'flex',
                flexWrap: 'nowrap',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',

                borderRadius: 2,
                padding: '6px',
                backgroundColor: 'grey.100',
              }}
            >
              {action}
            </Box>
          </Stack>
        </Popper>
      )}
    </Box>
  );
};

const CustomPagination = (
  props: Partial<TablePaginationProps> &
    PaginationPropsOverrides & { paginationLeftPart?: ReactNode },
) => {
  const { paginationLeftPart, ...rest } = props;

  return (
    <Grid container sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
      <Grid item sx={{ flexGrow: 2 }}>
        {paginationLeftPart}
      </Grid>

      <GridPagination {...rest} />
    </Grid>
  );
};
